import { useEffect, useMemo, useState } from 'react'

import { useQuery, useLazyQuery } from '@apollo/client'

import { CLICK_CODE, extractByTypename } from '@local/utils'

import { useErrorToast } from '@web/hooks/useErrorToast'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { openNewWindow, useGoTo } from '@web/hooks/useGoTo'
import { useHandleClickReviewWrite } from '@web/hooks/useHandleClickReviewWrite'
import useMe from '@web/hooks/useMe'
import { useNavigation } from '@web/hooks/useNavigation'
import useNMapScheme, { NMapSchemePrefix } from '@web/hooks/useNMapScheme'
import links from '@web/libs/constants/links'
import { Compare, compareVersion, mapsVersions } from '@web/libs/entry.helper'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { lils } from '@web/libs/nlog/locationPrivacy/locationPrivacy.constant'
import { callPhone, generatePath } from '@web/libs/utils'
import DailyBriefingMap from '@web/pages/v5/home/DailyBriefingSection/DailyBriefingMap'
import {
  convertBookingPlaceInfoToPrimitive,
  convertBookingToPrimitive,
  getFirstFocusBooking,
} from '@web/pages/v5/home/DailyBriefingSection/helper'
import { showDailyBriefingVar } from '@web/pages/v5/home/DailyBriefingSection/state'
import { useBookmark } from '@web/pages/v5/libs/bookmark/useBookmark'
import { useCurrentLocation } from '@web/pages/v5/libs/location/useCurrentLocation'
import { ClickEvent } from '@web/types'
import UIDailyBriefingSection from '@web/ui/pages/home/components/Sections/UIDailyBriefingSection'

import { DailyBriefingSectionQuery, DirectionsQuery, WeatherQuery } from './query'

export const DailyBriefingSection = () => {
  const { login } = useMe()
  const { data, error } = useQuery(DailyBriefingSectionQuery, { skip: !login })
  useErrorToast(error)
  const { goToDirectionLandingUrl } = useGoTo()
  const { goToReviewNewBridge } = useNavigation()
  const { isFromMapsApp, mapAppVersion, isAndroid } = useGlobalStaticStateContext()
  const { getNMapURLScheme } = useNMapScheme()
  const validateReviewWriteAccess = useHandleClickReviewWrite({
    blockUnavailable: true,
  })

  const { MeSucceed } = extractByTypename(data?.me)
  const todayBookings = MeSucceed?.todayBookings || []
  const total = todayBookings.length

  const [selectedBookingId, setSelectedBookingId] = useState(getFirstFocusBooking(todayBookings)?.id)
  const focusedBooking = todayBookings.find(booking => booking.id === selectedBookingId)
  const place = useMemo(() => convertBookingPlaceInfoToPrimitive(focusedBooking), [focusedBooking])
  const canShowDailyBriefingSection = !(total === 0 || !focusedBooking || !place)

  const { handleBookmark } = useBookmark()

  const { isAvailableBookmark, isBookmarked, openBookmarkModal } = handleBookmark({
    place:
      focusedBooking && focusedBooking.placeSummary
        ? {
            id: focusedBooking.placeSummary.id,
            isBookmarked: focusedBooking.placeSummary.isBookmarked,
            notExist: focusedBooking.placeSummary?.notExist,
          }
        : undefined,
  })

  const {
    coordinate: currentCoordinate,
    shortAddress: currentAddressName,
    loading: currentAddressLoading,
    refetchCurrentLocation,
  } = useCurrentLocation({
    lilsCode: lils.my.MY_DAILYBRIEFING_LOCATION,
    allowToGetLocation: canShowDailyBriefingSection,
  })

  const [getDirectionsQuery, { loading: directionsLoading, data: directionsData }] = useLazyQuery(DirectionsQuery)

  const { data: weatherData } = useQuery(WeatherQuery, {
    variables: {
      input: {
        coordinate: {
          latitude: place?.coordinate?.latitude as number,
          longitude: place?.coordinate?.longitude as number,
        },
      },
    },
    skip: !place?.coordinate,
  })

  useEffect(() => {
    if (currentCoordinate && place?.coordinate)
      getDirectionsQuery({
        variables: {
          input: {
            start: {
              coordinate: {
                latitude: currentCoordinate.latitude,
                longitude: currentCoordinate.longitude,
              },
            },
            goal: {
              coordinate: {
                latitude: place.coordinate.latitude,
                longitude: place.coordinate.longitude,
              },
            },
          },
        },
      })
  }, [getDirectionsQuery, currentCoordinate, place])

  if (!canShowDailyBriefingSection) {
    showDailyBriefingVar(false)
    return null
  }

  const handleClickBooking = (bookingId: string) => {
    setSelectedBookingId(bookingId)
  }

  const getIsOnClickMapValid = () => {
    if (isFromMapsApp && mapAppVersion) {
      if (place?.id) return true

      if (isAndroid || compareVersion(mapsVersions.ADDRESS_MARK_LANDING_SDK, mapAppVersion) > Compare.SMALLER) {
        return true
      }

      return false
    }

    return true
  }

  const handleClickMap = () => {
    sendClickLog({ area: CLICK_CODE['mydaily.poi'] })
    if (isFromMapsApp && mapAppVersion) {
      if (place?.id)
        return (window.location.href = getNMapURLScheme({
          path: 'place',
          prefix: NMapSchemePrefix.N_MAP,
          query: {
            id: place.id,
            mode: 'detail',
          },
        }))

      if (isAndroid || compareVersion(mapsVersions.ADDRESS_MARK_LANDING_SDK, mapAppVersion) > Compare.SMALLER) {
        return (window.location.href = getNMapURLScheme({
          path: 'place',
          prefix: NMapSchemePrefix.N_MAP,
          query: {
            name: focusedBooking.businessName,
            lat: place.coordinate.latitude,
            lng: place.coordinate.longitude,
            mode: 'summary',
          },
        }))
      }

      return undefined
    }

    if (place?.id) return openNewWindow(generatePath(links.PLACE_DETAIL, { placeId: place.id }))

    const address = focusedBooking.addressJson?.roadAddr || focusedBooking.addressJson?.jibun
    if (address) return openNewWindow(`${links.ADDRESS_DETAIL}${address}`)

    return undefined
  }

  const handleClickPhone = () => place?.phone && callPhone(place.phone)

  const onClickFindFastestPath = (directionLandingUrl: { appScheme?: string | null; webUrl?: string | null }) => {
    goToDirectionLandingUrl(directionLandingUrl)
  }

  const onClickReviewWrite = (e: ClickEvent) => {
    validateReviewWriteAccess(
      e,
      () => focusedBooking.visitId && goToReviewNewBridge({ visitId: focusedBooking.visitId }),
    )
  }

  const carDirections = directionsData?.directions.car?.summary
  const publicTransitDirections = directionsData?.directions.publicTransit?.summary
  const directions =
    carDirections && publicTransitDirections
      ? {
          car: carDirections,
          publicTransit: publicTransitDirections,
        }
      : null

  return (
    <UIDailyBriefingSection
      todayBookings={todayBookings.map(convertBookingToPrimitive)}
      focusedBooking={convertBookingToPrimitive(focusedBooking)}
      nowWeather={weatherData?.nowWeather || undefined}
      total={total}
      visitId={focusedBooking.visitId}
      onClickFindFastestPath={onClickFindFastestPath}
      handleClickBooking={handleClickBooking}
      handleClickPhone={handleClickPhone}
      currentAddressName={currentAddressName}
      currentAddressLoading={currentAddressLoading}
      onClickRefresh={refetchCurrentLocation}
      directions={directions}
      directionsLoading={directionsLoading}
      onClickReviewWrite={onClickReviewWrite}
      isAvailableBookmark={isAvailableBookmark}
      isBookmarked={isBookmarked}
      openBookmarkModal={openBookmarkModal}
    >
      <DailyBriefingMap onClick={handleClickMap} place={place} showOnClickMap={getIsOnClickMapValid()} />
    </UIDailyBriefingSection>
  )
}
